package com.fantasytenis.web.screens.skills

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.skills.entity.*
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.Skeleton
import com.fantasytenis.web.components.skills.*
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.borderTop
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Player Skill Detail Screen
 *
 * Comprehensive view of one player's skills including:
 * - Radar chart visualization
 * - All 5 skill bars with tiers
 * - Historical trends (sparklines)
 * - Similar players
 */
@Composable
fun PlayerSkillDetailScreen(playerId: String) {
    val colors = Theme.colors

    // Data states
    var playerHistory by remember { mutableStateOf<PlayerSkillHistory?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    // Load player data
    LaunchedEffect(playerId) {
        isLoading = true
        DependencyContainer.getPlayerSkillHistoryUseCase(playerId, weeks = 8).fold(
            onSuccess = { history ->
                playerHistory = history
                error = null
            },
            onFailure = { e ->
                error = e.message
            }
        )
        isLoading = false
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Header
        Header({
            style {
                flexShrink(0)
                backgroundColor(colors.primary)
                padding(16.px, 24.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
            }
        }) {
            // Back button
            Span({
                style {
                    fontSize(20.px)
                    color(Colors.White)
                    cursor("pointer")
                }
                onClick { NavigationState.goBack() }
            }) {
                Text("\u2190")
            }

            H2({
                style {
                    color(Colors.White)
                    fontSize(20.px)
                    fontWeight(700)
                    margin(0.px)
                }
            }) {
                Text(playerHistory?.playerName ?: "Player Skills")
            }
        }

        // Content
        Div({
            style {
                flex(1)
                overflow("auto")
                padding(24.px)
            }
        }) {
            if (isLoading) {
                // Loading skeleton
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(24.px)
                    }
                }) {
                    Skeleton(width = 100.percent, height = 200.px)
                    Skeleton(width = 100.percent, height = 300.px)
                }
            } else if (error != null) {
                // Error state
                Div({
                    style {
                        textAlign("center")
                        padding(32.px)
                        color(Colors.Error)
                    }
                }) {
                    Text("Failed to load player: $error")
                    Div({
                        style {
                            marginTop(16.px)
                        }
                    }) {
                        Button(
                            text = "Go Back",
                            onClick = { NavigationState.goBack() },
                            variant = "secondary"
                        )
                    }
                }
            } else {
                playerHistory?.let { history ->
                    PlayerSkillContent(history)
                }
            }
        }
    }
}

@Composable
private fun PlayerSkillContent(history: PlayerSkillHistory) {
    val colors = Theme.colors
    val costs = history.currentCosts

    // Radar chart section
    Section({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            border(1.px, LineStyle.Solid, colors.border)
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(colors.textPrimary)
                marginBottom(24.px)
                textAlign("center")
            }
        }) {
            Text("Skill Profile")
        }

        // Center the radar chart
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                marginBottom(24.px)
            }
        }) {
            RadarChart(costs = costs, size = 250, showLabels = true)
        }

        // Summary stats
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                gap(32.px)
                paddingTop(16.px)
                borderTop(1.px, LineStyle.Solid, colors.border)
            }
        }) {
            StatItem(label = "Total", value = "${costs.total}")
            StatItem(label = "Average", value = costs.average.asDynamic().toFixed(1) as String)
        }
    }

    // Skill breakdown section
    Section({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            border(1.px, LineStyle.Solid, colors.border)
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(colors.textPrimary)
                marginBottom(24.px)
            }
        }) {
            Text("Skill Breakdown")
        }

        Category.entries.forEach { category ->
            val cost = costs.getCostForCategory(category)
            val tier = SkillTier.fromCost(cost)
            val trend = history.getTrendForCategory(category)
            val historyCosts = history.history.map { it.costs.getCostForCategory(category) }

            Div({
                style {
                    marginBottom(20.px)
                }
            }) {
                // Category header
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(8.px)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                        }
                    }) {
                        Span({
                            style {
                                fontSize(14.px)
                                fontWeight(600)
                                color(getCategoryColor(category))
                            }
                        }) {
                            Text(category.displayName)
                        }
                        Span({
                            style {
                                fontSize(12.px)
                                color(colors.textSecondary)
                            }
                        }) {
                            Text("${getTierEmoji(tier)} ${tier.label}")
                        }
                    }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(12.px)
                        }
                    }) {
                        // Sparkline for history
                        if (historyCosts.isNotEmpty()) {
                            Sparkline(
                                values = historyCosts,
                                width = 60,
                                height = 20
                            )
                        }
                        TrendIndicator(trend, showDelta = true, size = 12)
                    }
                }

                // Skill bar
                SkillBar(
                    category = category,
                    value = cost,
                    showTier = false,
                    showLabel = false,
                    size = SkillBarSize.LARGE
                )
            }
        }
    }

    // Skill tiers explanation
    Section({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            border(1.px, LineStyle.Solid, colors.border)
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(colors.textPrimary)
                marginBottom(16.px)
            }
        }) {
            Text("Skill Tiers")
        }

        // Group skills by tier
        val skillsByTier = Category.entries.groupBy { SkillTier.fromCost(costs.getCostForCategory(it)) }

        SkillTier.entries.forEach { tier ->
            val skills = skillsByTier[tier] ?: emptyList()
            if (skills.isNotEmpty()) {
                Div({
                    style {
                        marginBottom(12.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(14.px)
                        }
                    }) {
                        Text("${getTierEmoji(tier)} ")
                    }
                    Span({
                        style {
                            fontWeight(600)
                            color(colors.textPrimary)
                        }
                    }) {
                        Text("${tier.label} (${tier.minCost}-${tier.maxCost}): ")
                    }
                    Span({
                        style {
                            color(colors.textSecondary)
                        }
                    }) {
                        Text(skills.joinToString(", ") { it.displayName })
                    }
                }
            }
        }
    }

    // History section
    if (history.history.isNotEmpty()) {
        Section({
            style {
                backgroundColor(colors.surface)
                borderRadius(12.px)
                padding(24.px)
                border(1.px, LineStyle.Solid, colors.border)
            }
        }) {
            H3({
                style {
                    fontSize(18.px)
                    fontWeight(600)
                    color(colors.textPrimary)
                    marginBottom(16.px)
                }
            }) {
                Text("Skill History")
            }

            // History table
            Div({
                style {
                    overflow("auto")
                }
            }) {
                Table({
                    style {
                        width(100.percent)
                        property("border-collapse", "collapse")
                        fontSize(13.px)
                    }
                }) {
                    Thead {
                        Tr {
                            Th({
                                style {
                                    textAlign("left")
                                    padding(8.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("Week")
                            }
                            Category.entries.forEach { category ->
                                Th({
                                    style {
                                        textAlign("center")
                                        padding(8.px)
                                        color(getCategoryColor(category))
                                    }
                                }) {
                                    Text(category.displayName.take(4))
                                }
                            }
                        }
                    }
                    Tbody {
                        history.history.take(6).forEach { snapshot ->
                            Tr {
                                Td({
                                    style {
                                        padding(8.px)
                                        color(colors.textSecondary)
                                    }
                                }) {
                                    Text(snapshot.weekOf)
                                }
                                Category.entries.forEach { category ->
                                    val cost = snapshot.costs.getCostForCategory(category)
                                    Td({
                                        style {
                                            textAlign("center")
                                            padding(8.px)
                                            fontWeight(600)
                                            color(colors.textPrimary)
                                        }
                                    }) {
                                        Text("$cost")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StatItem(label: String, value: String) {
    val colors = Theme.colors

    Div({
        style {
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(24.px)
                fontWeight(700)
                color(colors.primary)
            }
        }) {
            Text(value)
        }
        Div({
            style {
                fontSize(12.px)
                color(colors.textSecondary)
            }
        }) {
            Text(label)
        }
    }
}
